"use client";

import React, { useState, useEffect, useRef } from "react";
import { Mail, MapPin, Phone, ArrowUpRight, ExternalLink, ChevronLeft, ChevronRight } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function CentersNetworkConsumer({
  lang
}: {
  lang: "vi" | "en";
}) {
  const {
    posts,
    loading,
    error
  } = usePosts();
  const [selectedCenterId, setSelectedCenterId] = useState<string | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (posts.length > 0 && !selectedCenterId) {
      setSelectedCenterId(posts[0].id);
    }
  }, [posts, selectedCenterId]);
  const activeCenter = posts.find(p => p.id === selectedCenterId) || posts[0];
  const activeName = activeCenter?.customFields?.center_name || activeCenter?.title || "";
  const activeAddress = activeCenter?.customFields?.address || "";
  const activeDirectionsUrl = activeCenter?.customFields?.directions_url || "";
  const scroll = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      const offset = direction === "left" ? -320 : 320;
      scrollContainerRef.current.scrollBy({
        left: offset,
        behavior: "smooth"
      });
    }
  };
  return <div className="w-full space-y-6 pt-4 pb-8">
      {/* Section Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 border-b border-primary-foreground/15 pb-4">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="size-2 rounded-xs bg-secondary" />
            <span className="text-[11px] font-mono uppercase tracking-wider text-secondary font-medium">
              {lang === "vi" ? "Mạng lưới đào tạo" : "Network Presence"}
            </span>
          </div>
          <h3 className="font-serif text-lg sm:text-xl font-bold tracking-tight text-primary-foreground">
            {lang === "vi" ? "Hệ thống cơ sở ROBOTO" : "ROBOTO Center Network"}
          </h3>
          <p className="text-xs text-primary-foreground/75 mt-1 max-w-xl">
            {lang === "vi" ? "Lựa chọn cơ sở để xem địa chỉ và vị trí trên bản đồ." : "Select a center to see its address and map location."}
          </p>
        </div>

        {/* Scroll navigation arrows for desktop */}
        <div className="hidden sm:flex items-center gap-2 shrink-0">
          <Button type="button" variant="outline" size="icon" onClick={() => scroll("left")} aria-label="Scroll left" className="size-8 rounded-lg border-primary-foreground/20 bg-primary-foreground/5 text-primary-foreground hover:bg-primary-foreground/15 hover:text-primary-foreground">
            <ChevronLeft className="size-4" />
          </Button>
          <Button type="button" variant="outline" size="icon" onClick={() => scroll("right")} aria-label="Scroll right" className="size-8 rounded-lg border-primary-foreground/20 bg-primary-foreground/5 text-primary-foreground hover:bg-primary-foreground/15 hover:text-primary-foreground">
            <ChevronRight className="size-4" />
          </Button>
        </div>
      </div>

      {loading && <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 flex gap-4 overflow-hidden">
            {[...Array(3)].map((_, i) => <div key={i} className="w-72 h-44 rounded-lg bg-primary-foreground/5 animate-pulse shrink-0 border border-primary-foreground/10" />)}
          </div>
          <div className="lg:col-span-5 h-64 rounded-lg bg-primary-foreground/5 animate-pulse border border-primary-foreground/10" />
        </div>}

      {error && !loading && <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-xs text-destructive-foreground">
          {lang === "vi" ? "Không thể tải danh sách cơ sở. Vui lòng thử lại sau." : "Failed to load centers. Please try again later."}
        </div>}

      {!loading && !error && posts.length === 0 && <div className="rounded-lg border border-primary-foreground/10 bg-primary-foreground/5 p-6 text-center text-xs text-primary-foreground/70">
          {lang === "vi" ? "Đang cập nhật danh sách cơ sở." : "No centers found."}
        </div>}

      {!loading && posts.length > 0 && <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
          {/* (a) Horizontal scrolling card list */}
          <div className="lg:col-span-7 flex flex-col justify-between">
            <div ref={scrollContainerRef} className="flex gap-3.5 overflow-x-auto snap-x snap-mandatory pb-3 pt-1 -mx-1 px-1 scroll-smooth" style={{
          scrollbarWidth: "thin"
        }}>
              {posts.map((post, idx) => {
            const isSelected = activeCenter?.id === post.id;
            const centerName = post.customFields?.center_name || post.title || "";
            const address = post.customFields?.address || "";
            const phone = post.customFields?.phone || "";
            const image = {
              src: post.customFields?.image || "",
              srcset: "",
              sizes: ""
            };
            return <div key={post.id} data-index={idx} onClick={() => setSelectedCenterId(post.id)} className={`snap-start shrink-0 w-[270px] sm:w-[290px] rounded-lg border p-4 transition-colors cursor-pointer flex flex-col justify-between ${isSelected ? "border-secondary bg-secondary/15 ring-1 ring-secondary/50 shadow-md" : "border-primary-foreground/15 bg-primary-foreground/[0.04] hover:border-secondary/50 hover:bg-primary-foreground/[0.07]"}`}>
                    <div className="space-y-3">
                      {image.src && <div className="relative aspect-[16/9] w-full overflow-hidden rounded-md border border-primary-foreground/10 bg-black/20">
                          <img src={image.src} data-wvc-srcset={image.srcset} data-wvc-sizes={image.sizes} alt={centerName} loading="lazy" className="h-full w-full object-cover" />
                        </div>}

                      <div className="space-y-1">
                        <div className="flex items-center justify-between gap-2">
                          <h4 className="font-semibold text-primary-foreground text-xs line-clamp-1">
                            {centerName}
                          </h4>
                          {isSelected && <span className="shrink-0 text-[10px] font-mono text-secondary bg-secondary/20 px-1.5 py-0.5 rounded-sm">
                              {lang === "vi" ? "Đang chọn" : "Active"}
                            </span>}
                        </div>
                        <p className="text-[11px] text-primary-foreground/75 leading-relaxed flex items-start gap-1.5">
                          <MapPin className="size-3.5 shrink-0 text-secondary mt-0.5" />
                          <span className="line-clamp-2">{address}</span>
                        </p>
                      </div>
                    </div>

                    <div className="mt-4 pt-3 border-t border-primary-foreground/10 flex items-center justify-between gap-2">
                      {phone ? <a href={`tel:${phone.replace(/\s+/g, "")}`} onClick={e => e.stopPropagation()} className="text-[11px] font-mono text-secondary hover:underline flex items-center gap-1">
                          <Phone className="size-3" />
                          <span>{phone}</span>
                        </a> : <span className="text-[11px] text-primary-foreground/40 font-mono">
                          ROBOTO LAB
                        </span>}

                      <Button type="button" size="sm" onClick={e => {
                  e.stopPropagation();
                  setSelectedCenterId(post.id);
                }} className={`text-[11px] h-7 px-2.5 rounded-md ${isSelected ? "bg-secondary text-secondary-foreground hover:bg-secondary/90 font-medium" : "bg-primary-foreground/10 text-primary-foreground hover:bg-primary-foreground/20"}`}>
                        <MapPin className="size-3" />
                        <span>{lang === "vi" ? "Xem bản đồ" : "View Map"}</span>
                      </Button>
                    </div>
                  </div>;
          })}
            </div>
            <div className="text-[11px] text-primary-foreground/60 sm:hidden flex items-center gap-1.5 pt-1">
              <span>←</span>
              <span>{lang === "vi" ? "Vuốt để xem thêm cơ sở" : "Swipe to see more centers"}</span>
              <span>→</span>
            </div>
          </div>

          {/* (b) Google Maps embed & details */}
          <div className="lg:col-span-5 rounded-lg border border-primary-foreground/15 bg-primary-foreground/[0.04] p-4 flex flex-col justify-between gap-4">
            <div className="relative aspect-[16/10] sm:aspect-[16/9] lg:aspect-auto lg:h-52 w-full overflow-hidden rounded-md border border-primary-foreground/15 bg-black/40">
              {activeAddress ? <iframe title={activeName || "Center Map"} src={`https://maps.google.com/maps?q=${encodeURIComponent(activeAddress)}&t=&z=15&ie=UTF8&iwloc=&output=embed`} className="h-full w-full border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" /> : <div className="flex h-full w-full items-center justify-center p-4 text-center text-xs text-primary-foreground/60">
                  {lang === "vi" ? "Chưa có địa chỉ để hiển thị bản đồ" : "No map address available"}
                </div>}
            </div>

            <div className="space-y-2">
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                  <h4 className="font-serif text-sm font-bold text-primary-foreground">
                    {activeName}
                  </h4>
                  <p className="text-xs text-primary-foreground/80 leading-relaxed flex items-start gap-1.5">
                    <MapPin className="size-3.5 shrink-0 text-secondary mt-0.5" />
                    <span>{activeAddress}</span>
                  </p>
                </div>
              </div>

              {activeDirectionsUrl && <div className="pt-2">
                  <Button asChild size="sm" className="bg-accent text-accent-foreground font-semibold hover:bg-accent/90 rounded-lg text-xs h-8 px-3 inline-flex items-center gap-1.5">
                    <Link to={activeDirectionsUrl} newTab>
                      <span>{lang === "vi" ? "Mở Google Maps" : "Open Google Maps"}</span>
                      <ExternalLink className="size-3.5" />
                    </Link>
                  </Button>
                </div>}
            </div>
          </div>
        </div>}
    </div>;
}
export default function Footer() {
  const prefersReduced = useReducedMotion();
  const [lang, setLang] = useState<"vi" | "en">("vi");
  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = localStorage.getItem("roboto_lang");
    if (stored === "en" || stored === "vi") {
      setLang(stored);
    }
    const handleCustomChange = (e: Event) => {
      const detail = (e as CustomEvent<{
        lang: "vi" | "en";
      }>).detail;
      if (detail?.lang && (detail.lang === "vi" || detail.lang === "en")) {
        setLang(detail.lang);
      }
    };
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "roboto_lang" && (e.newValue === "vi" || e.newValue === "en")) {
        setLang(e.newValue);
      }
    };
    window.addEventListener("roboto-lang-change", handleCustomChange);
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleCustomChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);
  const curatedPrograms = [{
    titleVi: "Khóa học LEGO Robotics",
    titleEn: "LEGO Robotics Courses",
    to: "/ProgramsPage"
  }, {
    titleVi: "Đội tuyển thi đấu",
    titleEn: "Competition Squads",
    to: "/ProjectsCompetitionsPage"
  }, {
    titleVi: "Lộ trình Robotics",
    titleEn: "Robotics Pathway",
    to: "/RoboticsLearningPathwayPage"
  }];
  const exploreLinks = [{
    titleVi: "Trang chủ",
    titleEn: "Home",
    to: "/"
  }, {
    titleVi: "Về ROBOTO",
    titleEn: "About Us",
    to: "/AboutUsPage"
  }, {
    titleVi: "Chương trình",
    titleEn: "Programs",
    to: "/ProgramsPage"
  }, {
    titleVi: "Lộ trình Robotics",
    titleEn: "Robotics Pathway",
    to: "/RoboticsLearningPathwayPage"
  }, {
    titleVi: "Dự án & Thành tích",
    titleEn: "Projects & Achievements",
    to: "/ProjectsCompetitionsPage"
  }, {
    titleVi: "Hệ thống cơ sở",
    titleEn: "Centers",
    to: "/FacilitiesCentersPage"
  }];
  const centersWpQuery = new WP_Query({
    post_type: "center",
    per_page: 20,
    status: ["publish"]
  });
  return <footer id="footer" data-nav="dark" className="bg-background text-foreground relative w-full overflow-hidden">
      <div className="w-full bg-[#120a1f] text-primary-foreground relative border-t border-border">
        {/* Deep blueprint grid overlay with radial vignette for depth */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.06] bg-[linear-gradient(to_right,rgba(255,255,255,0.7)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.7)_1px,transparent_1px)] bg-[size:36px_36px]" />

        {/* Ambient radial lighting glow behind technical core */}
        <div aria-hidden="true" className="pointer-events-none absolute -top-40 left-1/4 h-96 w-96 rounded-lg bg-primary/15 blur-3xl" />
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-32 right-1/4 h-80 w-80 rounded-lg bg-secondary/10 blur-3xl" />

        {/* Refined engineering circuit-node telemetry and constellation overlay */}
        <svg aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full opacity-25 text-secondary" xmlns="http://www.w3.org/2000/svg" fill="none">
          {/* Top-right constellation with telemetry ring */}
          <g stroke="currentColor" strokeWidth="1" opacity="0.85">
            <line x1="84%" y1="12%" x2="89%" y2="18%" />
            <line x1="89%" y1="18%" x2="95%" y2="14%" />
            <line x1="89%" y1="18%" x2="92%" y2="28%" />
            <line x1="77%" y1="8%" x2="84%" y2="12%" />
            <circle cx="89%" cy="18%" r="7" strokeDasharray="2 2" strokeWidth="0.75" />
          </g>
          <g fill="currentColor">
            <circle cx="84%" cy="12%" r="2.5" />
            <circle cx="89%" cy="18%" r="3" />
            <circle cx="95%" cy="14%" r="2" />
            <circle cx="92%" cy="28%" r="2.5" />
            <circle cx="77%" cy="8%" r="2" />
          </g>

          {/* Left mid constellation with blueprint coordinate anchor */}
          <g stroke="currentColor" strokeWidth="1" opacity="0.75">
            <line x1="4%" y1="38%" x2="10%" y2="44%" />
            <line x1="10%" y1="44%" x2="16%" y2="40%" />
            <line x1="16%" y1="40%" x2="21%" y2="50%" />
            <line x1="10%" y1="44%" x2="8%" y2="56%" />
            <circle cx="10%" cy="44%" r="6" strokeDasharray="1.5 2" strokeWidth="0.75" />
          </g>
          <g fill="currentColor">
            <circle cx="4%" cy="38%" r="2" />
            <circle cx="10%" cy="44%" r="3" />
            <circle cx="16%" cy="40%" r="2.5" />
            <circle cx="21%" cy="50%" r="2" />
            <circle cx="8%" cy="56%" r="2.5" />
          </g>

          {/* Bottom-right connection nodes */}
          <g stroke="currentColor" strokeWidth="1" opacity="0.7">
            <line x1="72%" y1="76%" x2="78%" y2="82%" />
            <line x1="78%" y1="82%" x2="86%" y2="80%" />
            <line x1="86%" y1="80%" x2="90%" y2="90%" />
          </g>
          <g fill="currentColor">
            <circle cx="72%" cy="76%" r="2" />
            <circle cx="78%" cy="82%" r="2.5" />
            <circle cx="86%" cy="80%" r="3" />
            <circle cx="90%" cy="90%" r="2" />
          </g>

          {/* Mid-top balancing constellation */}
          <g stroke="currentColor" strokeWidth="1" opacity="0.7">
            <line x1="38%" y1="10%" x2="44%" y2="16%" />
            <line x1="44%" y1="16%" x2="50%" y2="12%" />
            <line x1="44%" y1="16%" x2="42%" y2="26%" />
          </g>
          <g fill="currentColor">
            <circle cx="38%" cy="10%" r="2" />
            <circle cx="44%" cy="16%" r="2.5" />
            <circle cx="50%" cy="12%" r="2" />
            <circle cx="42%" cy="26%" r="2.5" />
          </g>

          {/* Bottom-left precision node cluster */}
          <g stroke="currentColor" strokeWidth="1" opacity="0.65">
            <line x1="20%" y1="80%" x2="26%" y2="74%" />
            <line x1="26%" y1="74%" x2="33%" y2="78%" />
            <line x1="26%" y1="74%" x2="25%" y2="87%" />
            <circle cx="26%" cy="74%" r="5.5" strokeDasharray="2 2" strokeWidth="0.75" />
          </g>
          <g fill="currentColor">
            <circle cx="20%" cy="80%" r="2" />
            <circle cx="26%" cy="74%" r="2.5" />
            <circle cx="33%" cy="78%" r="2" />
            <circle cx="25%" cy="87%" r="2" />
          </g>
        </svg>

        <div className="relative mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 pt-16 pb-12">
          {/* Top blueprint spec rail with technical telemetry tag */}
          <div className="mb-12 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-primary-foreground/15 pb-6">
            <div className="flex items-center gap-3">
              <span className="h-2 w-2 rounded-xs bg-secondary" />
              <span className="font-mono text-xs uppercase tracking-widest text-secondary font-medium">
                {lang === "vi" ? "ROBOTO — HỌC VIỆN CÔNG NGHỆ & ROBOTICS" : "ROBOTO Incubator Framework"}
              </span>
            </div>
          </div>

          {/* Main Footer Columns - 4 rebalanced columns */}
          <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-12 lg:gap-8 pb-12">
            {/* Column 1: ROBOTO Brand & Intro (lg:col-span-4) */}
            <motion.div initial={prefersReduced ? false : {
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4
          }} className="lg:col-span-4 flex flex-col justify-between space-y-6">
              <div className="space-y-4">
                <div className="inline-block">
                  <Link to="/">
                    <WvcLogo className="h-12 w-auto" />
                  </Link>
                </div>

                <p className="text-xs leading-relaxed text-primary-foreground/80 max-w-sm">
                  {lang === "vi" ? "Học viện công nghệ và robotics cao cấp dành cho trẻ 4–16 tuổi, đào tạo lập trình robot, LEGO Education, tư duy thuật toán và kỹ thuật thực chiến trong môi trường phòng lab chuẩn quốc tế." : "An elite youth technology academy where students aged 4–16 master genuine robotics, LEGO Education, algorithmic coding, and hardware engineering inside professional lab environments."}
                </p>

                <div className="pt-1">
                  <span className="inline-flex items-center gap-2 rounded-md bg-secondary/15 px-2.5 py-1 text-[11px] font-mono text-secondary">
                    <span className="size-1.5 rounded-lg bg-secondary" />
                    CÔNG TY CP PHÁT TRIỂN TRÍ TUỆ VÀ TÀI NĂNG SÁNG TẠO MINDSHINE
                  </span>
                </div>
              </div>

              <div>
                <Button asChild className="bg-accent text-accent-foreground font-semibold hover:bg-accent/90 shadow-sm transition-colors rounded-lg text-xs px-4 py-2.5 h-auto inline-flex items-center gap-2">
                  <Link to="/FreeTrialContactPage">
                    <span>{lang === "vi" ? "Đặt lịch học thử" : "Book a free trial"}</span>
                    <ArrowUpRight className="size-3.5" />
                  </Link>
                </Button>
              </div>
            </motion.div>

            {/* Column 2: CHƯƠNG TRÌNH / Programs & Tracks (lg:col-span-2) */}
            <motion.div initial={prefersReduced ? false : {
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: 0.06
          }} className="lg:col-span-2 space-y-4">
              <h3 className="font-serif text-xs font-semibold tracking-wider text-primary-foreground uppercase border-b border-primary-foreground/15 pb-2">
                {lang === "vi" ? "CHƯƠNG TRÌNH" : "Programs & Tracks"}
              </h3>
              <ul className="space-y-2.5">
                {curatedPrograms.map((program, idx) => <li key={program.titleEn} data-index={idx}>
                    <Link to={program.to} className="text-xs text-primary-foreground/75 transition-colors hover:text-secondary inline-flex items-center gap-1.5">
                      <span className="size-1 rounded-lg bg-secondary/70" />
                      <span>{lang === "vi" ? program.titleVi : program.titleEn}</span>
                    </Link>
                  </li>)}
              </ul>
            </motion.div>

            {/* Column 3: KHÁM PHÁ / Explore (lg:col-span-3) */}
            <motion.div initial={prefersReduced ? false : {
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: 0.12
          }} className="lg:col-span-3 space-y-4">
              <h3 className="font-serif text-xs font-semibold tracking-wider text-primary-foreground uppercase border-b border-primary-foreground/15 pb-2">
                {lang === "vi" ? "KHÁM PHÁ" : "EXPLORE"}
              </h3>
              <ul className="space-y-2.5">
                {exploreLinks.map((link, idx) => <li key={link.to} data-index={idx}>
                    <Link to={link.to} className="text-xs text-primary-foreground/75 transition-colors hover:text-secondary inline-flex items-center gap-1.5">
                      <span className="size-1 rounded-lg bg-secondary/70" />
                      <span>{lang === "vi" ? link.titleVi : link.titleEn}</span>
                    </Link>
                  </li>)}
              </ul>
            </motion.div>

            {/* Column 4: LIÊN HỆ / Contact (lg:col-span-3) */}
            <motion.div initial={prefersReduced ? false : {
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: 0.18
          }} className="lg:col-span-3 space-y-4">
              <h3 className="font-serif text-xs font-semibold tracking-wider text-primary-foreground uppercase border-b border-primary-foreground/15 pb-2">
                {lang === "vi" ? "LIÊN HỆ" : "Contact"}
              </h3>

              <div className="space-y-3 text-xs text-primary-foreground/80">
                <div className="flex items-start gap-2">
                  <Phone className="size-3.5 shrink-0 text-secondary mt-0.5" />
                  <div className="text-[11px] font-mono leading-tight">
                    <a href="tel:0346386986" className="hover:text-secondary transition-colors font-medium">
                      0346 386 986
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <Mail className="size-3.5 shrink-0 text-secondary mt-0.5" />
                  <div className="flex flex-col text-[11px] leading-tight space-y-1">
                    <a href="mailto:info@robo.edu.vn" className="hover:text-secondary transition-colors underline-offset-2 hover:underline">
                      info@robo.edu.vn
                    </a>
                    <a href="mailto:info@mindshine.edu.vn" className="hover:text-secondary transition-colors underline-offset-2 hover:underline text-primary-foreground/70">
                      info@mindshine.edu.vn
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* New full-width section: ROBOTO Center Network */}
          <PostsProvider wp_query={{
          post_type: "center",
          per_page: 20,
          status: ["publish"]
        }} className="w-full">
            <CentersNetworkConsumer lang={lang} />
          </PostsProvider>

          <Separator className="bg-primary-foreground/15 my-6" />

          {/* Bottom Bar: Policy Links & Legal Copyright */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-2 text-xs text-primary-foreground/75">
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-x-5 gap-y-2 text-[11px]">
              <Link to="#" className="hover:text-secondary transition-colors underline-offset-2 hover:underline">
                {lang === "vi" ? "Chính sách bảo mật" : "Privacy Policy"}
              </Link>
              <span className="text-primary-foreground/30">•</span>
              <Link to="#" className="hover:text-secondary transition-colors underline-offset-2 hover:underline">
                {lang === "vi" ? "Chính sách thanh toán" : "Payment Policy"}
              </Link>
              <span className="text-primary-foreground/30">•</span>
              <Link to="#" className="hover:text-secondary transition-colors underline-offset-2 hover:underline">
                {lang === "vi" ? "Chính sách và điều kiện nhập học" : "Admission Policy"}
              </Link>
            </div>

            <p className="text-center md:text-right text-[11px] text-primary-foreground/65 leading-relaxed max-w-xl">
              {lang === "vi" ? `© ${new Date().getFullYear()} ROBOTO — CÔNG TY CP PHÁT TRIỂN TRÍ TUỆ VÀ TÀI NĂNG SÁNG TẠO MINDSHINE. Bảo lưu mọi quyền.` : `© ${new Date().getFullYear()} ROBOTO — MindShine Intelligence and Creative Talent Development JSC. All rights reserved.`}
            </p>
          </div>
        </div>
      </div>
    </footer>;
}